import { BailOut } from '../strategies'
import FormErrors from '../../form_errors'
import { find } from 'lodash'

const mockStrategy = {
  // defaults, will be overriden below when not in production
  accept: async () => false,
  process: () => {
    return Promise.reject(new BailOut())
  }
}

// If we're running under Jest/Node, this will be globally defined.
const isNode = typeof env !== 'undefined'

if ('production' !== FONTAWESOME.ENV && isNode) {
  const mocks = typeof MOCKS !== 'undefined' ? MOCKS : {} // eslint-disable-line no-undef

  if (Object.keys(mocks).length > 0) {
    /* eslint-disable no-console */
    console.groupCollapsed('Current data mock settings')
    console.log(`Development Notice (this will not appear in production):`)

    // Initialize mock options based on what the Define Plugin has loaded from the mocks on disk
    Object.keys(mocks).forEach((type) => {
      const postKey = `mock.${type}.post`
      const postStartingValue = localStorage.getItem(postKey)

      const getKey = `mock.${type}.get`
      const getStartingValue = localStorage.getItem(getKey)

      const putKey = `mock.${type}.put`
      const putStartingValue = localStorage.getItem(putKey)

      const patchKey = `mock.${type}.patch`
      const patchStartingValue = localStorage.getItem(patchKey)

      const deleteKey = `mock.${type}.delete`
      const deleteStartingValue = localStorage.getItem(deleteKey)

      if (!postStartingValue) localStorage.setItem(postKey, 'none')
      if (!getStartingValue) localStorage.setItem(getKey, 'none')
      if (!putStartingValue) localStorage.setItem(putKey, 'none')
      if (!patchStartingValue) localStorage.setItem(patchKey, 'none')
      if (!deleteStartingValue) localStorage.setItem(deleteKey, 'none')

      console.log(`\t\t${postKey}: ${localStorage.getItem(postKey)}`)
      console.log(`\t\t${putKey}: ${localStorage.getItem(putKey)}`)
      console.log(`\t\t${patchKey}: ${localStorage.getItem(patchKey)}`)
      console.log(`\t\t${deleteKey}: ${localStorage.getItem(deleteKey)}`)
      console.log(`\t\t${getKey}: ${localStorage.getItem(getKey)}`)
    })

    console.log(`\tSet any of these in localStorage to control mocks. Valid values for each are:`)
    console.log(`\t\tnone`)
    console.log(`\t\tsuccess`)
    console.log(`\t\tfail-gracefully`)
    console.log(`\t\tdie-horribly`)
    console.log(`\tMock responses should be declared in assets/static/js/mocks/data/<type>/<method>.js`)

    console.groupEnd()
    /* eslint-enable no-console */

    const baseUrlTypeMap = Object.keys(mocks).reduce((accOuter, type) => {
      return {
        ...accOuter,
        ...Object.keys(mocks[type]).reduce((accInner, method) => {
          if (mocks[type][method].baseUrl) {
            return { ...accInner, [mocks[type][method].baseUrl]: type }
          } else {
            return accInner
          }
        }, {})
      }
    }, {})

    const getMockOption = ({ type, url, method }) => {
      return localStorage.getItem(`mock.${type}.${method}`)
    }

    const resolveType = ({ type, url }) => {
      return type ? type : find(baseUrlTypeMap, (baseUrl) => url.match(baseUrl))
    }

    const defaultFailResponse = {
      response: {
        status: 422,
        data: {
          version: '1.0',
          errors: [
            {
              title: 'Gracefully handled error',
              detail: 'graceful error message placeholder',
              failure: 'fail_gracefully'
            }
          ]
        }
      }
    }

    const defaultDieResponse = new Error()

    const defaultSuccessResponse = {
      status: 200,
      statusText: 'OK',
      data: {
        jsonapi: { version: '1.0' },
        data: {}
      }
    }

    const processSuccess = (params = {}) => {
      const { type, method, response = defaultSuccessResponse, url } = params

      const resolvedType = resolveType({ type, url })

      if (!method) {
        throw new Error('Missing required param: method')
      }

      if ('object' !== typeof response) {
        throw new Error("Parameter 'response' must be an object.")
      }

      console.log(`mocking type: ${resolvedType}, method: ${method} with success`) // eslint-disable-line no-console
      return response
    }

    const processFailGracefully = (params = {}) => {
      const { type, method, response = defaultFailResponse, url } = params

      const resolvedType = resolveType({ type, url })

      if (!method) {
        throw new Error('Missing required param: method')
      }

      if ('object' !== typeof response) {
        throw new Error("Parameter 'response' must be an object.")
      }

      console.log(`mocking type: ${resolvedType}, method: ${method} with fail-gracefully`) // eslint-disable-line no-console
      return response
    }

    const processDieHorribly = (params = {}) => {
      const { type, method, response = defaultDieResponse, url } = params

      const resolvedType = resolveType({ type, url })

      if (!method) {
        throw new Error('Missing required param: method')
      }

      if ('object' !== typeof response) {
        throw new Error("Parameter 'response' must be an object.")
      }

      console.log(`mocking type: ${resolvedType}, method: ${method} with die-horribly`) // eslint-disable-line no-console
      return response
    }

    const extractResponse = async (scenario, params = {}) => {
      const { type, method, url } = params
      const resolvedType = resolveType({ type, url })

      if (mocks && resolvedType && method && ~['success', 'failGracefully', 'dieHorribly'].indexOf(scenario)) {
        const declaredResponse = mocks[resolvedType][method][scenario]
        if ('function' === typeof declaredResponse) {
          return await declaredResponse(params)
        } else {
          return declaredResponse
        }
      }
      // intentionally return undefined by default
    }

    mockStrategy.accept = async ({ type, url, method }) => {
      const resolvedType = resolveType({ type, url })
      const mockOption = getMockOption({ type: resolvedType, url, method })

      switch (mockOption) {
        case 'success':
        case 'fail-gracefully':
        case 'die-horribly':
          return resolvedType && method && !!mocks[resolvedType][method]
        default:
          return false
      }
    }

    mockStrategy.process = async (params = {}) => {
      const { type, method, url } = params
      const resolvedType = resolveType({ type, url })
      const mockOptionKey = `mock.${resolvedType}.${method}`
      const mockOption = getMockOption({ type: resolvedType, method })

      switch (mockOption) {
        case 'success':
          try {
            return processSuccess({ ...params, response: await extractResponse('success', params) })
          } catch (error) {
            console.error(`Mocking success on type=${resolvedType}, method=${method} was not supposed to result in error, but we got:`, error) // eslint-disable-line no-console
            throw error
          }
        case 'fail-gracefully':
          try {
            return processFailGracefully({ ...params, response: await extractResponse('failGracefully', params) })
          } catch (error) {
            if (!(error instanceof FormErrors)) {
              console.error(`Mocking fail-gracefully on type=${resolvedType}, method=${method} was supposed to result in FormErrors, but we got:`, error) // eslint-disable-line no-console
            }
            throw error
          }
        case 'die-horribly':
          try {
            return processDieHorribly({ ...params, response: await extractResponse('dieHorribly', params) })
          } catch (error) {
            console.error(
              `Mocking dieHorribly on type=${resolvedType}, method=${method}.\n` + // eslint-disable-line no-console
                `\tHere's the error we got--hopefully, it's what you were expecting: `,
              error
            )
            throw error
          }
        case 'none':
          console.warn(
            `Ignoring attempt to process data request with method=${method} using mock strategy \n` + // eslint-disable-line no-console
              `configured with option ${mockOptionKey}=${mockOption}.`
          )
          return Promise.reject(new BailOut())
        default:
          console.warn(
            `You tried to initialize ${mockOptionKey} with the value "${mockOption}", but I don't recognize that option.\n` + // eslint-disable-line no-console
              `\tI'll pretend that you don't want a mock here.`
          )
          return Promise.reject(new BailOut())
      }
    }
  }
}

export default mockStrategy
